import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bot, Send, Sparkles, X } from 'lucide-react';
import { api } from '../lib/api';

export default function GigFlowBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "I am connected to your live database. 🧠\nAsk me anything like:\n• \"Analyze my spending habits\"\n• \"Why is my income dropping?\"\n• \"Predict my next month\"",
      sender: 'bot'
    }
  ]);

  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const handleSend = async (textOverride = null) => {
    const text = textOverride || input;
    if (!text.trim()) return;

    setMessages(prev => [...prev, { id: Date.now(), text: text, sender: 'user' }]);
    setInput('');
    setIsTyping(true);

    try {
      const res = await api.post(`/chat`, { message: text });
      setMessages(prev => [...prev, { id: Date.now() + 1, text: res.data.reply, sender: 'bot' }]);
    } catch (error) {
      setMessages(prev => [...prev, { id: Date.now() + 1, text: "⚠️ Brain freeze. Backend connection failed.", sender: 'bot' }]);
    }

    setIsTyping(false);
  };

  return (
    <>
      <motion.button
        className="chatbot-fab"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
      >
        {isOpen ? <X size={24} /> : <Bot size={28} />}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="chatbot-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
          >
            <div className="chatbot-header">
              <div className="header-info">
                <div className="bot-avatar-container"><Sparkles size={20} /></div>
                <div className="header-text"><h4>GigFlow AI</h4><span>Financial Co-Pilot 🧠</span></div>              </div>
              <button onClick={() => setIsOpen(false)} className="btn-icon-micro"><X size={18} /></button>
            </div>

            <div className="chatbot-messages">
              {messages.map(msg => (
                <div key={msg.id} className={`chat-bubble ${msg.sender}`}>
                  {msg.sender === 'bot' && <div className="bubble-icon"><Bot size={14} /></div>}
                  <div className="bubble-content">
                    {msg.sender === 'bot' ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ node, ...props }) => <p style={{ margin: '0 0 8px 0' }} {...props} />,
                          strong: ({ node, ...props }) => <span style={{ color: '#10B981', fontWeight: '700' }} {...props} />,
                          ul: ({ node, ...props }) => <ul style={{ margin: '5px 0', paddingLeft: '20px' }} {...props} />,
                          li: ({ node, ...props }) => <li style={{ marginBottom: '4px' }} {...props} />
                        }}
                      >
                        {msg.text}
                      </ReactMarkdown>
                    ) : (
                      msg.text
                    )}
                  </div>
                </div>
              ))}
              {isTyping && <div className="chat-bubble bot"><div className="bubble-icon"><Bot size={14} /></div><div className="bubble-content typing"><span className="dot"></span><span className="dot"></span><span className="dot"></span></div></div>}
              <div ref={messagesEndRef} />
            </div>

            <div className="chatbot-chips">
              <button onClick={() => handleSend("Give me a financial summary")}>📊 Summary</button>
              <button onClick={() => handleSend("What is my best earning day?")}>📅 Best Day</button>
              <button onClick={() => handleSend("Forecast my next month")}>🔮 Forecast</button>
            </div>

            <div className="chatbot-input-area">
              <input
                type="text" placeholder="Ask anything..."
                value={input} onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              />
              <button className="btn-send" onClick={() => handleSend()} disabled={!input.trim()}><Send size={18} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
